import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Brand } from './Brand.jsx';
import { PlayerStrip } from './PlayerStrip.jsx';
import { DiceTray } from './DiceTray.jsx';
import { ScoreSheet } from './ScoreSheet.jsx';
import { ActionPanel } from './ActionPanel.jsx';
import { ActivityPanel } from './ActivityPanel.jsx';
import { Ranking } from './Ranking.jsx';
import { loadDiceEffects, playDiceRollSound, saveDiceEffects, vibrateForRoll } from '../lib/diceEffects.js';
import { recommendMoves } from '../../shared/game.mjs';

const ROLL_ANIMATION_MS = 860;
const ROLL_FALLBACK_MS = 10_500;
function diceKey(dice) {
  if (!dice) return null;
  return [dice.white1, dice.white2, dice.red, dice.yellow, dice.green, dice.blue].join(':');
}

export function GameScreen({ state, busy, connected, onAction, onLeave }) {
  const viewerPlayer = state.players.find((player) => player.id === state.viewer.id);
  const [selectedId, setSelectedId] = useState(viewerPlayer?.id || state.players[0]?.id);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [diceEffects, setDiceEffects] = useState(loadDiceEffects);
  const [isRolling, setIsRolling] = useState(false);
  const lastDiceKey = useRef(diceKey(state.dice));
  const localRollPending = useRef(false);
  const settleTimer = useRef(null);
  const fallbackTimer = useRef(null);

  useEffect(() => {
    if (viewerPlayer && !selectedId) setSelectedId(viewerPlayer.id);
  }, [viewerPlayer, selectedId]);

  useEffect(() => {
    const tick = () => setRemainingSeconds(state.settings.turnTimerSeconds === 0 || !state.deadline ? 0 : Math.max(0, Math.ceil((state.deadline - Date.now()) / 1000)));
    tick();
    const timer = setInterval(tick, 250);
    return () => clearInterval(timer);
  }, [state.deadline]);

  useEffect(() => {
    saveDiceEffects(diceEffects);
    if (diceEffects.reducedMotion && isRolling && state.dice) setIsRolling(false);
  }, [diceEffects, isRolling, state.dice]);

  useEffect(() => () => {
    window.clearTimeout(settleTimer.current);
    window.clearTimeout(fallbackTimer.current);
  }, []);

  useEffect(() => {
    const nextKey = diceKey(state.dice);
    if (!nextKey) {
      lastDiceKey.current = null;
      return;
    }
    if (nextKey === lastDiceKey.current) return;

    lastDiceKey.current = nextKey;
    const startedLocally = localRollPending.current;
    localRollPending.current = false;
    window.clearTimeout(fallbackTimer.current);
    window.clearTimeout(settleTimer.current);

    if (diceEffects.reducedMotion) {
      setIsRolling(false);
      return;
    }

    setIsRolling(true);
    if (!startedLocally && diceEffects.sound) void playDiceRollSound();
    settleTimer.current = window.setTimeout(() => setIsRolling(false), ROLL_ANIMATION_MS);
  }, [state.dice, diceEffects.reducedMotion, diceEffects.sound]);

  const selectedPlayer = useMemo(() => state.players.find((player) => player.id === selectedId) || viewerPlayer || state.players[0], [state.players, selectedId, viewerPlayer]);
  const canInteract = !state.timerPaused
    && !isRolling
    && selectedPlayer?.id === state.viewer.id
    && state.viewer.role === 'player'
    && ((state.phase === 'public' && !viewerPlayer?.submittedPublic) || (state.phase === 'colored' && state.viewer.isActive));
  const availableMoves = isRolling ? [] : state.phase === 'public' ? state.viewerMoves.public : state.phase === 'colored' ? state.viewerMoves.colored : [];
  const childHelpActive = Boolean(viewerPlayer?.childMode && selectedPlayer?.id === viewerPlayer.id);
  const recommendedMoves = childHelpActive && canInteract ? recommendMoves(viewerPlayer.board, availableMoves) : [];
  const recommendPass = childHelpActive && canInteract && recommendedMoves.length === 0;

  function chooseMove(move) {
    if (isRolling) return;
    if (state.phase === 'public') onAction('game:submitPublic', { move });
    if (state.phase === 'colored') onAction('game:submitColored', { move });
  }

  function confirmLeave() {
    if (state.status === 'playing' && !window.confirm('Laufende Partie wirklich verlassen? Du kannst über den Einladungslink wieder beitreten.')) return;
    onLeave();
  }

  async function rollDice() {
    if (busy || state.timerPaused || isRolling || !state.viewer.isActive || state.phase !== 'await-roll') return;

    localRollPending.current = true;
    setIsRolling(true);
    window.clearTimeout(fallbackTimer.current);

    if (diceEffects.sound) void playDiceRollSound();
    if (diceEffects.haptics) vibrateForRoll();

    fallbackTimer.current = window.setTimeout(() => {
      localRollPending.current = false;
      setIsRolling(false);
    }, ROLL_FALLBACK_MS);

    const ok = await onAction('game:roll');
    if (!ok) {
      localRollPending.current = false;
      window.clearTimeout(fallbackTimer.current);
      setIsRolling(false);
    }
  }

  return (
    <main className="app-shell game-shell">
      <header className="topbar game-topbar">
        <Brand compact />
        <div className="room-mini"><span>Raum</span><strong>{state.code}</strong></div>
        <div className={`connection-pill compact ${connected ? 'is-online' : 'is-offline'}`}><span />{connected ? 'Live' : 'Offline'}</div>
        {state.status === 'playing' && state.viewer.role === 'player' && (
          <button className={`button button--small ${state.timerPaused ? 'button--primary' : 'button--ghost'}`} disabled={busy || (!connected && !state.timerPaused)} onClick={() => onAction('game:togglePause')}>
            {state.timerPaused ? '▶ Fortsetzen' : 'Ⅱ Pause'}
          </button>
        )}
        <button className="button button--ghost button--small game-leave" aria-label="Verlassen" onClick={confirmLeave}><span className="game-leave__full">Verlassen</span><span className="game-leave__short" aria-hidden="true">×</span></button>
      </header>

      <PlayerStrip players={state.players} activePlayerId={state.activePlayerId} viewerId={state.viewer.id} selectedId={selectedPlayer?.id} onSelect={setSelectedId} />
      {state.timerPaused && <div className="pause-banner" role="status"><strong>Partie pausiert</strong><span>{state.pauseReason === 'connection' ? 'Warte auf die Wiederverbindung aller Spieler.' : 'Die Zeit und alle Spielaktionen sind angehalten.'}</span></div>}

      <div className="game-layout">
        <section className="game-main">
          <DiceTray
            dice={state.dice}
            lockedRows={state.lockedRows}
            whiteTotal={state.whiteTotal}
            rolling={isRolling}
            effects={diceEffects}
            onEffectsChange={setDiceEffects}
          />
          <ActionPanel
            state={state}
            viewerPlayer={viewerPlayer}
            remainingSeconds={remainingSeconds}
            timerPaused={state.timerPaused || !connected}
            busy={busy || state.timerPaused}
            isRolling={isRolling}
            onRoll={rollDice}
            onAction={onAction}
            childHelpActive={childHelpActive}
            recommendPass={recommendPass}
          />

          {selectedPlayer && (
            <ScoreSheet
              player={selectedPlayer}
              lockedRows={state.lockedRows}
              availableMoves={availableMoves}
              recommendedMoves={recommendedMoves}
              childGuidance={childHelpActive && canInteract}
              interactive={canInteract && !busy}
              onMove={chooseMove}
            />
          )}

          {state.status === 'finished' && <Ranking ranking={state.ranking} />}

          <details className="all-sheets">
            <summary>Alle Wertungsblätter</summary>
            <div className="all-sheets__grid">
              {state.players.filter((player) => player.id !== selectedPlayer?.id).map((player) => <ScoreSheet key={player.id} player={player} lockedRows={state.lockedRows} compact />)}
            </div>
          </details>
        </section>

        <ActivityPanel state={state} onAction={onAction} />
      </div>
    </main>
  );
}
